import { useCallback, useEffect, useId, useState, type Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, FilePenLine, Glasses, History, Stethoscope } from 'lucide-react';
import {
  PdType,
  PERMISSIONS,
  type DispensingPrescription,
  type DispensingPrescriptionSummary,
  type EyePrescription,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { Select } from '@/components/ui/Select';
import { useLocale } from '@/hooks/useLocale';
import { usePermissions } from '@/hooks/usePermissions';
import { useCustomerMeasurementsQuery } from '@/modules/measurements/api/measurements.api';
import { formatDate } from '@/utils/format';
import { apiErrorCode } from '@/services/apiClient';
import { cn } from '@/utils/cn';
import { ClinicalEyeGrid } from '@/modules/medical/components/ClinicalEyeGrid';
import type { EyeForm, FormErrors } from '@/modules/medical/examinationForm';
import {
  useDispensingPrescriptionQuery,
  useDispensingPrescriptionsQuery,
} from '../api/opticalOrders.api';
import {
  externalProblems,
  latestAuthoritativePd,
  PAIR_USAGES,
  pdEmpty,
  prescriptionField,
  type BuilderAction,
  type ExternalPrescriptionForm,
  type PairDraft,
} from '../builder/draft';
import { formatCalendarDate } from '../builder/format';
import type { BuilderProblem } from '../builder/refusals';

interface PrescriptionStepProps {
  customerId: string;
  pair: PairDraft;
  /** Pairs before this one that already have a prescription, for the explicit copy action. */
  copyFrom: { pairId: string; number: number }[];
  /** Null where the step only corrects one job's prescription: the usage choice belongs to building an order. Otherwise whether another pair still fits on the order. */
  canAddPair: boolean | null;
  dispatch: Dispatch<BuilderAction>;
  problems: BuilderProblem[];
  showErrors: boolean;
}

/**
 * Step 1: what the glasses are for, and which prescription this pair is made to.
 *
 * External comes first, because most customers bring one; it is preselected only when the customer has no usable prescription from this shop. A clinical prescription is never picked for the user, not even the newest one. A clinical prescription is picked by its exact number and shown read-only — the order sends its id alone and the server copies it. An external one is typed into the same eye grid the clinic uses, left to right in every language with the right eye above the left.
 */
export function PrescriptionStep({
  customerId,
  pair,
  copyFrom,
  canAddPair,
  dispatch,
  problems,
  showErrors,
}: PrescriptionStepProps) {
  const { t } = useTranslation();
  const legendId = useId();
  const usageLegendId = useId();
  const [confirmSplit, setConfirmSplit] = useState(false);
  const { source } = pair.prescription;

  const choose = (next: 'CLINICAL' | 'EXTERNAL') =>
    dispatch({ type: 'chooseSource', pairId: pair.id, source: next });

  const options = [
    {
      value: 'EXTERNAL' as const,
      label: t('opticalOrders.source.external'),
      hint: t('opticalOrders.source.externalHint'),
      Icon: FilePenLine,
    },
    {
      value: 'CLINICAL' as const,
      label: t('opticalOrders.source.clinical'),
      hint: t('opticalOrders.source.clinicalHint'),
      Icon: Stethoscope,
    },
  ];

  return (
    <div className="space-y-5">
      {canAddPair === null ? null : (
        <>
          <fieldset aria-labelledby={usageLegendId} className="space-y-2">
            <legend id={usageLegendId} className="text-ink text-sm font-medium">
              {t('opticalOrders.usage.question')}
            </legend>
            <div className="flex flex-wrap gap-2">
              {PAIR_USAGES.map((usage) => (
                <label
                  key={usage}
                  className={cn(
                    'inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm',
                    'focus-within:ring-brand-200 focus-within:ring-2',
                    pair.usage === usage
                      ? 'border-brand-600 bg-brand-50 text-brand-800 font-medium'
                      : 'border-line text-ink bg-white',
                  )}
                >
                  <input
                    type="radio"
                    name={`usage-${pair.id}`}
                    value={usage}
                    checked={pair.usage === usage}
                    onChange={() => dispatch({ type: 'setUsage', pairId: pair.id, usage })}
                  />
                  {t(`opticalOrders.usage.options.${usage}`)}
                </label>
              ))}
            </div>
            {pair.usage === 'DISTANCE_NEAR' ? (
              <div className="border-line space-y-2 rounded-lg border border-dashed p-3">
                <p className="text-muted text-sm">{t('opticalOrders.usage.splitHint')}</p>
                {canAddPair ? (
                  <Button variant="secondary" size="sm" onClick={() => setConfirmSplit(true)}>
                    <Glasses className="size-4" aria-hidden />
                    {t('opticalOrders.usage.split')}
                  </Button>
                ) : (
                  <p className="text-sm text-amber-800">{t('opticalOrders.usage.splitLimit')}</p>
                )}
              </div>
            ) : null}
            {pair.usage === 'MULTIFOCAL' ? (
              <p className="text-muted text-xs">{t('opticalOrders.usage.multifocalHint')}</p>
            ) : null}
          </fieldset>

          <ConfirmDialog
            isOpen={confirmSplit}
            title={t('opticalOrders.usage.splitTitle')}
            message={t('opticalOrders.usage.splitMessage')}
            confirmLabel={t('opticalOrders.usage.split')}
            onCancel={() => setConfirmSplit(false)}
            onConfirm={() => {
              setConfirmSplit(false);
              dispatch({ type: 'splitDistanceNear', pairId: pair.id });
            }}
          />
        </>
      )}

      <fieldset aria-labelledby={legendId} className="space-y-3">
        <legend id={legendId} className="text-ink text-sm font-medium">
          {t('opticalOrders.source.question')}
        </legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {options.map(({ value, label, hint, Icon }) => (
            <label
              key={value}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-xl border p-4',
                'focus-within:ring-brand-200 focus-within:ring-2',
                source === value ? 'border-brand-600 bg-brand-50' : 'border-line bg-white',
              )}
            >
              <input
                type="radio"
                name={`source-${pair.id}`}
                value={value}
                checked={source === value}
                onChange={() => choose(value)}
                className="mt-1"
              />
              <span className="min-w-0">
                <span className="text-ink flex items-center gap-2 font-medium">
                  <Icon className="size-4" aria-hidden />
                  {label}
                </span>
                <span className="text-muted mt-0.5 block text-sm">{hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {copyFrom.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {copyFrom.map((other) => (
            <Button
              key={other.pairId}
              variant="secondary"
              size="sm"
              onClick={() =>
                dispatch({ type: 'copyPrescription', fromPairId: other.pairId, toPairId: pair.id })
              }
            >
              <Copy className="size-4" aria-hidden />
              {t('opticalOrders.source.copyFrom', { number: other.number })}
            </Button>
          ))}
        </div>
      ) : null}

      {source === 'CLINICAL' ? (
        <ClinicalPicker
          customerId={customerId}
          pair={pair}
          dispatch={dispatch}
          problems={problems}
          showErrors={showErrors}
        />
      ) : null}

      {source === 'EXTERNAL' ? (
        <ExternalPrescriptionFields
          customerId={customerId}
          autoFillPd={canAddPair !== null}
          pair={pair}
          dispatch={dispatch}
          problems={problems}
          showErrors={showErrors}
        />
      ) : null}

      {source === null && showErrors ? (
        <p className="text-sm text-red-600" role="alert">
          {t('opticalOrders.validation.chooseSource')}
        </p>
      ) : null}
    </div>
  );
}

function ClinicalPicker({
  customerId,
  pair,
  dispatch,
  problems,
  showErrors,
}: {
  customerId: string;
  pair: PairDraft;
  dispatch: Dispatch<BuilderAction>;
  problems: BuilderProblem[];
  showErrors: boolean;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const legendId = useId();
  const [showUnusable, setShowUnusable] = useState(false);
  const usable = useDispensingPrescriptionsQuery(customerId);
  const everything = useDispensingPrescriptionsQuery(customerId, true, showUnusable);
  const { refetch } = usable;
  const letGo = useCallback(
    () => dispatch({ type: 'clinicalUnavailable', pairIds: [pair.id] }),
    [dispatch, pair.id],
  );
  const refresh = useCallback(() => void refetch(), [refetch]);
  const selectedId = pair.prescription.clinicalPrescriptionId;
  const unavailable =
    pair.prescription.clinicalUnavailable ||
    problems.some((problem) => problem.field === 'clinical');

  const list = usable.data ?? [];
  const unusable = (everything.data ?? []).filter((entry) => entry.usability !== 'USABLE');

  const describe = (entry: DispensingPrescriptionSummary) =>
    [
      t('opticalOrders.clinical.issued', { date: formatCalendarDate(entry.issuedOn, locale) }),
      entry.expiresOn
        ? t('opticalOrders.clinical.expires', { date: formatCalendarDate(entry.expiresOn, locale) })
        : t('opticalOrders.clinical.noExpiry'),
    ].join(' · ');

  if (usable.isPending) return <LoadingState />;
  if (usable.isError) {
    return (
      <Alert tone="error">
        {t('opticalOrders.clinical.loadError')}{' '}
        <Button variant="secondary" size="sm" onClick={refresh}>
          {t('common.retry')}
        </Button>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {unavailable ? (
        <Alert tone="error">
          <span className="block font-medium">{t('opticalOrders.clinical.noLongerAvailable')}</span>
          <span className="block">{t('opticalOrders.clinical.chooseAgain')}</span>
        </Alert>
      ) : null}

      {list.length === 0 ? (
        <div className="border-line space-y-3 rounded-xl border border-dashed p-4">
          <p className="text-ink text-sm">{t('opticalOrders.clinical.none')}</p>
          <p className="text-muted text-sm">{t('opticalOrders.clinical.noneHint')}</p>
          <Button
            variant="secondary"
            onClick={() => dispatch({ type: 'chooseSource', pairId: pair.id, source: 'EXTERNAL' })}
          >
            <FilePenLine className="size-4" aria-hidden />
            {t('opticalOrders.clinical.enterExternal')}
          </Button>
        </div>
      ) : (
        <fieldset aria-labelledby={legendId} className="space-y-2">
          <legend id={legendId} className="text-ink text-sm font-medium">
            {t('opticalOrders.clinical.title')}
          </legend>
          <ul className="space-y-2">
            {list.map((entry) => (
              <li key={entry.clinicalPrescriptionId}>
                <label
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5',
                    'focus-within:ring-brand-200 focus-within:ring-2',
                    selectedId === entry.clinicalPrescriptionId
                      ? 'border-brand-600 bg-brand-50'
                      : 'border-line bg-white',
                  )}
                >
                  <input
                    type="radio"
                    name={`clinical-${pair.id}`}
                    value={entry.clinicalPrescriptionId}
                    checked={selectedId === entry.clinicalPrescriptionId}
                    onChange={() =>
                      dispatch({
                        type: 'chooseClinical',
                        pairId: pair.id,
                        clinicalPrescriptionId: entry.clinicalPrescriptionId,
                      })
                    }
                    className="mt-1"
                  />
                  <span className="min-w-0">
                    <span className="text-ink block font-mono text-sm font-medium" dir="ltr">
                      {entry.prescriptionNumber}
                    </span>
                    <span className="text-muted block text-xs">{describe(entry)}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {showErrors && !selectedId ? (
            <p className="text-sm text-red-600" role="alert">
              {t('opticalOrders.validation.chooseClinical')}
            </p>
          ) : null}
        </fieldset>
      )}

      <label className="text-muted flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={showUnusable}
          onChange={(event) => setShowUnusable(event.target.checked)}
        />
        {t('opticalOrders.clinical.showUnusable')}
      </label>
      {showUnusable ? (
        everything.isPending ? (
          <LoadingState />
        ) : unusable.length === 0 ? (
          <p className="text-muted text-sm">{t('opticalOrders.clinical.noUnusable')}</p>
        ) : (
          <ul className="text-muted space-y-1 text-sm">
            {unusable.map((entry) => (
              <li key={entry.clinicalPrescriptionId}>
                <span className="font-mono" dir="ltr">
                  {entry.prescriptionNumber}
                </span>{' '}
                · {describe(entry)} ·{' '}
                {t('opticalOrders.clinical.unusable', {
                  reason: t(`sales.opticalJobs.dispensingUsability.${entry.usability}`),
                })}
              </li>
            ))}
          </ul>
        )
      ) : null}

      {selectedId ? (
        <ClinicalPreview
          customerId={customerId}
          prescriptionId={selectedId}
          onUnusable={letGo}
          onRefresh={refresh}
        />
      ) : null}
    </div>
  );
}

const toEyeForm = (eye: EyePrescription): EyeForm => ({
  sphere: eye.sphere ?? '',
  cylinder: eye.cylinder ?? '',
  axis: eye.axis === null ? '' : String(eye.axis),
  add: eye.add ?? '',
  prism: eye.prism ?? '',
  prismBase: eye.prismBase ?? '',
});

/** The chosen prescription's final values, read-only: the order copies them from the prescription itself, never from this screen. */
function ClinicalPreview({
  customerId,
  prescriptionId,
  onUnusable,
  onRefresh,
}: {
  customerId: string;
  prescriptionId: string;
  onUnusable: () => void;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();
  const preview = useDispensingPrescriptionQuery(customerId, prescriptionId);
  const failure = preview.isError ? apiErrorCode(preview.error) : null;

  useEffect(() => {
    if (failure === 'CLINICAL_PRESCRIPTION_NOT_USABLE' || failure === 'NOT_FOUND') {
      onUnusable();
      onRefresh();
    }
  }, [failure, onUnusable, onRefresh]);

  if (preview.isPending) return <LoadingState />;
  if (preview.isError) {
    return failure === 'CLINICAL_PRESCRIPTION_NOT_USABLE' || failure === 'NOT_FOUND' ? null : (
      <Alert tone="error">{t('opticalOrders.clinical.previewError')}</Alert>
    );
  }

  return <ClinicalPreviewCard prescription={preview.data} />;
}

/** The PD a prescription carries. Each value is its own left-to-right unit, so a label in the page's language never mixes with the numbers when the line wraps. */
export function PdSummary({
  pd,
}: {
  pd: {
    pdType: PdType | null;
    rightPdMm: string | null;
    leftPdMm: string | null;
    totalPdMm: string | null;
  };
}) {
  const { t } = useTranslation();
  const parts = [
    pd.rightPdMm ? { key: 'right', value: `R ${pd.rightPdMm} mm` } : null,
    pd.leftPdMm ? { key: 'left', value: `L ${pd.leftPdMm} mm` } : null,
    pd.totalPdMm
      ? {
          key: 'total',
          label: t('opticalOrders.clinical.binocular'),
          value: `${pd.totalPdMm} mm`,
        }
      : null,
  ].filter((part): part is { key: string; label?: string; value: string } => part !== null);
  return (
    <p className="text-sm">
      <span className="text-muted">
        {t('opticalOrders.clinical.pd')}
        {pd.pdType ? ` (${t(`measurements.pdTypes.${pd.pdType}`)})` : ''}:
      </span>{' '}
      {parts.length === 0 ? (
        <span className="text-ink">—</span>
      ) : (
        parts.map((part, index) => (
          <span key={part.key} className="inline-block whitespace-nowrap">
            {index > 0 ? (
              <span aria-hidden className="text-muted px-1.5">
                ·
              </span>
            ) : null}
            {part.label ? <span className="text-muted">{part.label} </span> : null}
            <span className="text-ink font-mono" dir="ltr">
              {part.value}
            </span>
          </span>
        ))
      )}
    </p>
  );
}

function ClinicalPreviewCard({ prescription }: { prescription: DispensingPrescription }) {
  const { t } = useTranslation();
  return (
    <section
      className="border-line space-y-3 rounded-xl border bg-white p-4"
      aria-label={t('opticalOrders.clinical.previewLabel')}
    >
      <div>
        <h3 className="text-ink text-sm font-semibold">
          {t('opticalOrders.clinical.preview')}{' '}
          <span className="font-mono" dir="ltr">
            {prescription.prescriptionNumber}
          </span>
        </h3>
        <p className="text-muted text-xs">{t('opticalOrders.clinical.previewHint')}</p>
      </div>
      <ClinicalEyeGrid
        caption={t('opticalOrders.clinical.gridCaption', {
          number: prescription.prescriptionNumber,
        })}
        value={{ right: toEyeForm(prescription.right), left: toEyeForm(prescription.left) }}
      />
      <PdSummary pd={prescription.pd} />
    </section>
  );
}

function ExternalPrescriptionFields({
  customerId,
  autoFillPd,
  pair,
  dispatch,
  problems,
  showErrors,
}: {
  customerId: string;
  /** False where a recorded job prescription is being corrected: its PD is never refilled. */
  autoFillPd: boolean;
  pair: PairDraft;
  dispatch: Dispatch<BuilderAction>;
  problems: BuilderProblem[];
  showErrors: boolean;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermissions();
  const pdLegendId = useId();
  const form = pair.prescription.external;
  const measurements = useCustomerMeasurementsQuery(
    autoFillPd && can(PERMISSIONS.MEASUREMENTS_VIEW) ? customerId : undefined,
  );
  const saved = latestAuthoritativePd(measurements.data ?? [], customerId, form.pdType);
  const savedId = saved?.id ?? null;
  const filledFrom = form.pdSource?.measurementId ?? null;
  const empty = pdEmpty(form);

  // Fills the PD from the latest authoritative measurement of the pair's PD type, unless someone typed it. A filled PD follows a change of PD type, and is emptied when that type has no saved measurement.
  useEffect(() => {
    if (!autoFillPd || form.pdManual || !measurements.isSuccess) return;
    if (saved) {
      if (filledFrom !== saved.id && (filledFrom !== null || empty)) {
        dispatch({ type: 'applySavedPd', pairId: pair.id, measurement: saved, byUser: false });
      }
    } else if (filledFrom !== null) {
      dispatch({ type: 'clearSavedPd', pairId: pair.id });
    }
    // `saved` is identified by its id; the object itself is rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoFillPd,
    form.pdManual,
    measurements.isSuccess,
    savedId,
    filledFrom,
    empty,
    dispatch,
    pair.id,
  ]);
  const edit = (patch: Partial<ExternalPrescriptionForm>) =>
    dispatch({ type: 'editExternal', pairId: pair.id, patch });

  const errors: FormErrors = { ...(showErrors ? externalProblems(form) : {}) };
  for (const problem of problems) {
    if (problem.field.startsWith('rx.')) errors[problem.field] = problem.message;
  }
  const message = (field: string) => {
    const error = errors[`rx.${field}`];
    return error ? t(error.key, error.values) : undefined;
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <h3 className="text-ink text-sm font-semibold">{t('opticalOrders.external.title')}</h3>
        <p className="text-muted text-xs">{t('medical.refraction.hint')}</p>
        <ClinicalEyeGrid
          caption={t('opticalOrders.external.gridCaption')}
          value={{ right: form.right, left: form.left }}
          onChange={(next) => edit({ right: next.right, left: next.left })}
          errors={errors}
          errorPath={prescriptionField}
        />
      </div>

      <fieldset aria-labelledby={pdLegendId} className="space-y-3">
        <legend id={pdLegendId} className="text-ink text-sm font-semibold">
          {t('opticalOrders.external.pd')}
        </legend>
        <div className="flex flex-wrap gap-4 text-sm">
          {(['BINOCULAR', 'MONOCULAR'] as const).map((mode) => (
            <label key={mode} className="text-ink flex items-center gap-2">
              <input
                type="radio"
                name={`pd-mode-${pair.id}`}
                checked={form.pdMode === mode}
                onChange={() => edit({ pdMode: mode })}
              />
              {t(`opticalOrders.external.${mode === 'BINOCULAR' ? 'binocular' : 'monocular'}`)}
            </label>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {form.pdMode === 'BINOCULAR' ? (
            <Input
              label={t('opticalOrders.external.totalPd')}
              dir="ltr"
              inputMode="decimal"
              autoComplete="off"
              placeholder="63.5"
              className="font-mono rtl:text-right"
              value={form.totalPdMm}
              error={message('totalPdMm')}
              onChange={(event) => edit({ totalPdMm: event.target.value })}
            />
          ) : (
            <>
              <Input
                label={t('opticalOrders.external.rightPd')}
                dir="ltr"
                inputMode="decimal"
                autoComplete="off"
                placeholder="31.5"
                className="font-mono rtl:text-right"
                value={form.rightPdMm}
                error={message('rightPdMm')}
                onChange={(event) => edit({ rightPdMm: event.target.value })}
              />
              <Input
                label={t('opticalOrders.external.leftPd')}
                dir="ltr"
                inputMode="decimal"
                autoComplete="off"
                placeholder="32.0"
                className="font-mono rtl:text-right"
                value={form.leftPdMm}
                error={message('leftPdMm')}
                onChange={(event) => edit({ leftPdMm: event.target.value })}
              />
            </>
          )}
          <FormField label={t('opticalOrders.external.pdType')} error={message('pdType')}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                hasError={Boolean(message('pdType'))}
                value={form.pdType}
                options={[
                  { value: PdType.DISTANCE, label: t('opticalOrders.external.pdTypes.DISTANCE') },
                  { value: PdType.NEAR, label: t('opticalOrders.external.pdTypes.NEAR') },
                ]}
                onChange={(event) => edit({ pdType: event.target.value as PdType })}
              />
            )}
          </FormField>
        </div>
        {form.pdSource ? (
          <p className="text-muted flex items-center gap-1.5 text-xs" role="status">
            <History className="size-3.5 shrink-0" aria-hidden />
            {t('opticalOrders.external.pdFromMeasurement', {
              date: formatDate(form.pdSource.measuredAt, locale),
              source: t(`measurements.sources.${form.pdSource.source}`),
            })}
          </p>
        ) : form.pdManual && autoFillPd ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className="text-muted" role="status">
              {t('opticalOrders.external.pdTyped')}
            </span>
            {saved ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  dispatch({
                    type: 'applySavedPd',
                    pairId: pair.id,
                    measurement: saved,
                    byUser: true,
                  })
                }
              >
                <History className="size-4" aria-hidden />
                {t('opticalOrders.external.useSavedPd')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label={t('opticalOrders.external.reference')}
          hint={t('opticalOrders.external.optional')}
          autoComplete="off"
          value={form.externalReference}
          maxLength={64}
          error={message('externalReference')}
          onChange={(event) => edit({ externalReference: event.target.value })}
        />
        <Input
          label={t('opticalOrders.external.prescriber')}
          hint={t('opticalOrders.external.optional')}
          autoComplete="off"
          value={form.prescriberName}
          maxLength={120}
          error={message('prescriberName')}
          onChange={(event) => edit({ prescriberName: event.target.value })}
        />
        <Input
          type="date"
          label={t('opticalOrders.external.prescribedOn')}
          hint={t('opticalOrders.external.optional')}
          dir="ltr"
          value={form.prescribedOn}
          error={message('prescribedOn')}
          onChange={(event) => edit({ prescribedOn: event.target.value })}
        />
        <Input
          type="date"
          label={t('opticalOrders.external.expiresOn')}
          hint={t('opticalOrders.external.optional')}
          dir="ltr"
          value={form.expiresOn}
          error={message('expiresOn')}
          onChange={(event) => edit({ expiresOn: event.target.value })}
        />
      </div>

      <p className="text-muted text-xs">{t('opticalOrders.external.serverChecks')}</p>
    </div>
  );
}
